import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Grid,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { postFallRecord, postGraphData } from '../../../utils/api';
import { useMutation, useQueryClient } from 'react-query';
import TextInput from '../../../components/TextInput';
import { HEALTH_OVERVIEW_MODAL_TYPES } from '../../../constants';

const AddDataModal = ({ open, handleClose, dataType, userId }) => {
  const today = new Date().toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(today);
  const [fromTime, setFromTime] = useState('');
  const [toDate, setToDate] = useState(today);
  const [toTime, setToTime] = useState('');
  const [value, setValue] = useState('');
  const [secondaryValue, setSecondaryValue] = useState('');
  const [notes, setNotes] = useState('');
  const [location, setLocation] = useState('');
  // Add error state variables
  const [valueError, setValueError] = useState(false);
  const [secondaryValueError, setSecondaryValueError] = useState(false);

  const unit = HEALTH_OVERVIEW_MODAL_TYPES[dataType]?.unit || '';
  const type = HEALTH_OVERVIEW_MODAL_TYPES[dataType]?.type || '';
  const type2 = HEALTH_OVERVIEW_MODAL_TYPES[dataType]?.type2 || '';
  const isBloodPressure = dataType === 'Blood Pressure';
  // Add queryClient instance
  const queryClient = useQueryClient();

  // Move mutation setup outside handleSubmit
  const mutation = useMutation({
    mutationFn: (data) => {
      if (dataType === 'Fall detection') {
        return postFallRecord({ userId, healthData: data });
      }
      return postGraphData({ userId, healthData: data });
    },
    onSuccess: () => {
      if (dataType !== 'Fall detection') {
        queryClient.invalidateQueries(['vitalsGraph']);
        queryClient.invalidateQueries(['activityGraph']);
      }
      if (dataType === 'Fall detection') {
        queryClient.invalidateQueries(['fallDetectionGraph']);
      }
      if (dataType === 'Gait Speed') {
        queryClient.invalidateQueries(['gaitSpeedGraph']);
      }
      resetForm();
      handleClose();
    },
  });

  const handleSubmit = () => {
    setValueError(false);
    setSecondaryValueError(false);
    let hasError = false;
    if (!value && dataType !== 'Fall detection' && dataType !== 'Sleep') {
      setValueError(true);
      hasError = true;
    }

    if (isBloodPressure && !secondaryValue) {
      setSecondaryValueError(true);
      hasError = true;
    }

    if (hasError) {
      return;
    }

    if (dataType === 'Fall detection') {
      const fallData = {
        dateTime: moment(`${fromDate} ${fromTime}`).toISOString(),
        type: 'Drop',
        location: location,
        notes: notes,
        source: 'manual',
      };
      mutation.mutate(fallData);
      return;
    }

    const baseData = {
      userId: userId,
      dateFrom: moment(`${fromDate} ${fromTime}`).toDate(),
      dateTo: toTime
        ? moment(`${toDate} ${toTime}`).toDate()
        : moment(`${fromDate} ${fromTime}`).add(1, 'minute').toDate(),
      sourcePlatform: 'manual',
      sourceDeviceId: 'MANUAL_WEB',
      sourceId: 'MANUAL_WEB',
      sourceName: 'MANUAL_WEB',
      recordingMethod: 'manual',
      workoutSummary: null,
      metadata: {
        note: notes,
        location: '',
      },
      platform: 'MANUAL_WEB',
      phoneName: 'MANUAL_WEB',
      phoneId: 'MANUAL_WEB',
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Calculate sleep duration in minutes if dataType is Sleep
    const sleepDuration =
      dataType === 'Sleep' && toTime
        ? moment(`${toDate} ${toTime}`).diff(moment(`${fromDate} ${fromTime}`), 'minutes', true)
        : Number(value);

    const healthData = isBloodPressure
      ? [
          {
            ...baseData,
            uuid: crypto.randomUUID(),
            value: {
              __type: 'NumericHealthValue',
              numericValue: Number(secondaryValue),
            },
            type: type,
            unit: unit,
          },
          {
            ...baseData,
            uuid: crypto.randomUUID(),
            value: {
              __type: 'NumericHealthValue',
              numericValue: Number(value),
            },
            type: type2,
            unit: unit,
          },
        ]
      : [
          {
            ...baseData,
            uuid: crypto.randomUUID(),
            value: {
              __type: 'NumericHealthValue',
              numericValue: dataType === 'Sleep' ? sleepDuration : Number(value),
            },
            type: type,
            unit: unit,
          },
        ];

    mutation.mutate(healthData);
  };
  const resetForm = () => {
    setFromDate(today);
    setFromTime('');
    setToDate(today);
    setToTime('');
    setValue('');
    setSecondaryValue('');
    setNotes('');
    setLocation('');
    setValueError(false);
    setSecondaryValueError(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '8px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          Add {dataType}
        </Typography>
        <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '16px 24px' }}>
        <Grid container spacing={3} sx={{ marginTop: '2px' }}>
          <Grid item xs={12} sm={6}>
            <TextInput
              label="From Time"
              type="time"
              value={fromTime}
              onChange={(e) => setFromTime(e.target.value)}
              fullWidth
              margin="normal"
              inputProps={{
                step: 300,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextInput
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              fullWidth
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          {(dataType === 'Calories' || dataType === 'Sleep') && (
            <>
              <Grid item xs={12} sm={6}>
                <TextInput
                  label="To Time"
                  type="time"
                  value={toTime}
                  onChange={(e) => setToTime(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    step: 300, // 5 min
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInput
                  label="To Date"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </>
          )}

          {isBloodPressure ? (
            <>
              <Grid item xs={12} sm={6}>
                <TextInput
                  label="Systolic"
                  type="number"
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                    setValueError(false);
                  }}
                  fullWidth
                  margin="normal"
                  error={valueError}
                  helperText={valueError ? 'This field is required' : ''}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInput
                  label="Diastolic"
                  type="number"
                  value={secondaryValue}
                  onChange={(e) => {
                    setSecondaryValue(e.target.value);
                    setSecondaryValueError(false);
                  }}
                  fullWidth
                  margin="normal"
                  error={secondaryValueError}
                  helperText={secondaryValueError ? 'This field is required' : ''}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
                  }}
                />
              </Grid>
            </>
          ) : (
            dataType !== 'Sleep' && (
              <Grid item xs={12}>
                <TextInput
                  label={`${dataType}`}
                  type={dataType === 'Fall detection' ? 'text' : 'number'}
                  value={dataType === 'Fall detection' ? 'DROP' : value}
                  onChange={(e) => {
                    setValue(e.target.value);
                    setValueError(false);
                  }}
                  fullWidth
                  margin="normal"
                  error={valueError}
                  helperText={valueError ? 'This field is required' : ''}
                  disabled={dataType === 'Fall detection'}
                />
              </Grid>
            )
          )}

          {dataType === 'Fall detection' && (
            <Grid item xs={12}>
              <TextInput
                label="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                fullWidth
                margin="normal"
                placeholder="e.g. Living Room, Kitchen, etc."
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextInput
              label="Notes"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="Add any additional notes here..."
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button
          onClick={handleClose}
          sx={{
            borderRadius: '50px',
            textTransform: 'none',
            padding: '8px 16px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            borderRadius: '50px',
            textTransform: 'none',
            padding: '8px 24px',
            backgroundColor: '#3b9b8f',
            '&:hover': {
              backgroundColor: '#2d8a7e',
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDataModal;
